import { useTranslation } from "react-i18next";
import { Tile } from "@carbon/react";
import { FunnelCounts } from "../../../types";
import "./analyticsCharts.scss";

interface SourcingFunnelProps {
  funnel: FunnelCounts;
  /** Overrides the default "Sourcing funnel" heading. */
  title?: string;
}

// Same token + literal-fallback convention as the other analytics charts (no `--cds-chart-*`
// tokens ship with this Carbon version). A blue ramp, one step darker per stage.
const STAGES: Array<{ key: keyof FunnelCounts; color: string }> = [
  { key: "requests", color: "var(--cds-support-info, #4589ff)" },
  { key: "outForQuote", color: "var(--cds-link-primary, #0f62fe)" },
  { key: "awarded", color: "var(--cds-button-primary, #0043ce)" },
  { key: "delivered", color: "var(--cds-border-inverse, #002d9c)" },
];

const SourcingFunnel = ({ funnel, title }: SourcingFunnelProps) => {
  const { t } = useTranslation();
  const heading = title ?? t("analytics.funnel.title");
  // The funnel is NOT strictly monotonic: awarded can exceed requests (direct orders with no
  // purchase-request line, split awards). Scale bars to the largest stage so none overflows its
  // track, and show conversion as-is (it may exceed 100%).
  const max = Math.max(0, ...STAGES.map(({ key }) => funnel[key]));

  if (max <= 0) {
    return (
      <Tile className="analytics-chart">
        <h3 className="analytics-chart__title">{heading}</h3>
        <p className="analytics-chart__empty">{t("analytics.funnel.noData")}</p>
      </Tile>
    );
  }

  return (
    <Tile className="analytics-chart">
      <h3 className="analytics-chart__title">{heading}</h3>
      <div className="analytics-funnel">
        {STAGES.map(({ key, color }) => {
          const count = funnel[key];
          const width = (Math.max(0, count) / max) * 100;
          const conversion =
            funnel.requests > 0
              ? `${Math.round((count / funnel.requests) * 100)}%`
              : "—";
          return (
            <div
              key={key}
              className="analytics-funnel__stage"
              data-testid={`funnel-stage-${key}`}
            >
              <div className="analytics-funnel__meta">
                <span className="analytics-funnel__label">
                  {t(`analytics.funnel.${key}`)}
                </span>
                <span className="analytics-funnel__figures">
                  <span className="analytics-funnel__count">{count}</span>
                  <span className="analytics-funnel__conversion">
                    <span>{conversion}</span>{" "}
                    <span>{t("analytics.funnel.ofRequests")}</span>
                  </span>
                </span>
              </div>
              <div className="analytics-funnel__track">
                <div
                  className="analytics-funnel__bar"
                  data-testid={`funnel-bar-${key}`}
                  style={{ width: `${width}%`, background: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Tile>
  );
};

export default SourcingFunnel;
